import React from 'react';
import { Field, Note, Select, T, TextInput } from './ui';

/**
 * Which single account a campaign is aimed at.
 *
 * The sibling of `ChannelPicker`, with one control it does not have: a search box. That is the
 * whole difference between the two sets. There are twenty channels worth ranking by following and
 * there are twelve thousand accounts, so a bare dropdown of "the first twenty" would answer a
 * question nobody asked — and the account an operator wants here is one they already know the name
 * or the address of, because they are about to go and open its inbox.
 *
 * Searchable on the address as well as the name, for the same reason: on a development environment
 * the address *is* the identifier. Nobody remembers what they called the test account; everybody
 * remembers which mailbox they can open.
 *
 * ## The address is shown, twice
 *
 * Once in each option, and once under the control for the selected account. That is deliberate
 * duplication: the option text is what an operator reads while choosing, and the line underneath is
 * what they read while pressing send, which is a different moment and a different question. The
 * second one comes from the server's live count response rather than from this list, so a stale
 * option cannot be the thing that decides where a campaign goes.
 *
 * ## The saved account that is no longer in the list
 *
 * Same treatment as the channel picker's, for the same reason — a campaign saved against an account
 * that has since been suspended must still show who it named, or reopening it would read as though
 * nobody had ever been chosen, and saving would write that loss back.
 */
const RecipientPicker = ({
  recipients = [],
  userId,
  recipientLabel,
  recipientEmail,
  search,
  onSearch,
  loading,
  onSelect,
  unavailable,
  // Passed by the Campaign Builder: this is the campaign's audience, so it is a required field.
  selectId,
  required,
  error
}) => {
  const inList = recipients.some((recipient) => recipient.id === userId);

  const optionLabel = (recipient) =>
    recipient.email ? `${recipient.label} — ${recipient.email}` : recipient.label;

  return (
    <>
      <Field
        label="Find an account"
        hint="Matches the start of a name, a username or an email address. Leave it empty for the twenty newest accounts."
      >
        <TextInput
          type="search"
          value={search}
          placeholder="Name, @username or email"
          onChange={(event) => onSearch(event.target.value)}
        />
      </Field>

      <Field label="Recipient" required={required} error={error}>
        {loading && !recipients.length ? (
          <Note>Searching accounts…</Note>
        ) : !recipients.length && !userId ? (
          /*
            An empty result is not an empty control. Unlike the channel picker's version of this,
            the likeliest cause here is a search term that matches nothing rather than a platform
            with no channels on it — so the sentence points at the box above.
          */
          <Note style={{ color: T.amber }}>
            {search
              ? 'No account starts with that. Try fewer characters, or the email address.'
              : 'No accounts with an email address were found.'}
          </Note>
        ) : (
          <Select
            id={selectId}
            value={userId || ''}
            onChange={(event) => {
              const next = recipients.find((recipient) => recipient.id === event.target.value);
              onSelect(next || null);
            }}
          >
            <option value="">Choose an account</option>

            {/*
              The saved account, kept selectable-looking even when it is outside the results.

              Without this, reopening a campaign and then typing a search that does not match its
              recipient would blank the dropdown — the operator would watch their choice vanish
              because of something they typed in a different control.
            */}
            {userId && !inList ? (
              <option value={userId}>
                {unavailable
                  ? `${recipientLabel || 'Saved recipient'} — no longer available`
                  : recipientLabel || 'Saved recipient'}
              </option>
            ) : null}

            {recipients.map((recipient) => (
              <option key={recipient.id} value={recipient.id}>
                {optionLabel(recipient)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {/*
        Where it is actually going, in the largest terms this control can manage.

        The one fact an operator has to be sure of before sending a campaign to a named individual,
        and the one the option text is too crowded to state plainly. Announced as well as shown,
        because changing the selection changes where real email goes and a screen-reader user got
        no feedback from the version of this that only redrew a dropdown.
      */}
      {userId && recipientEmail ? (
        <Note role="status" aria-live="polite">
          This campaign will be sent to <strong>{recipientEmail}</strong> and to nobody else.
        </Note>
      ) : null}

      {recipients.length >= 20 ? (
        <Note>Showing twenty accounts. Narrow the search to find another.</Note>
      ) : null}
    </>
  );
};

export default RecipientPicker;
